import { Outlet, useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { AppBrandMark } from "./AppBrandMark";
import { CLAIMS_MODAL_CLOSE_MESSAGE } from "../../lib/demoMode";

const POPUP_TITLES: Record<string, string> = {
  "/popup/recovered": "Value Recovered",
  "/popup/potential": "Potential Claims",
  "/popup/rejected": "Rejected Claims",
};

function popupTitle(pathname: string): string {
  if (pathname.includes("/dispute-thread")) return "Dispute Thread";
  if (/^\/popup\/cil-(disallowed-to-allowed|allowed-to-disallowed)\/[^/]+/.test(pathname)) return "Changed event";
  if (pathname.startsWith("/popup/cil-file/") && pathname.endsWith("/from-allowed")) return "Changed events from Allowed: this file";
  if (pathname.startsWith("/popup/cil-file/")) return "Changed events from Disallowed: this file";
  if (pathname.startsWith("/popup/cil-coverage")) return "Every ruling checked";
  if (pathname.startsWith("/popup/trueup-gaps")) return "True-up gaps";
  if (pathname.startsWith("/popup/cil-disallowed-to-allowed")) return "Changed events from Disallowed";
  if (pathname.startsWith("/popup/cil-allowed-to-disallowed")) return "Changed events from Allowed";
  return POPUP_TITLES[pathname] ?? "Claim Detail";
}

/**
 * Chrome for the claims view (shown in a modal over the Overview): just
 * a title bar and a close button - no sidebar, no HP Genie widget - so the
 * window reads as a focused viewer for one category of claims and their
 * detail (overview, value breakdown, precedent, matching clause).
 */
export function PopupShell() {
  const { pathname } = useLocation();
  // Embedded in the Overview modal -> ask the parent to close it; opened on its own -> close the window.
  const closeView = () => {
    if (window.parent !== window) window.parent.postMessage(CLAIMS_MODAL_CLOSE_MESSAGE, window.location.origin);
    else window.close();
  };

  return (
    <div className="flex min-h-dvh flex-col bg-zinc-50/80">
      <header className="sticky top-0 z-30 flex flex-none items-center justify-between gap-4 border-b border-zinc-200/80 bg-white px-4 py-3 sm:px-6">
        <div className="flex min-w-0 items-center gap-3">
          <AppBrandMark variant="compact" />
          <h1 className="truncate font-serif text-lg font-semibold text-zinc-900 sm:text-xl">
            {popupTitle(pathname)}
          </h1>
        </div>
        <button
          type="button"
          onClick={closeView}
          className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-600 transition-colors hover:bg-zinc-50 hover:text-zinc-900"
        >
          <X className="h-4 w-4" aria-hidden />
          Close
        </button>
      </header>

      <main className="min-w-0 flex-1 px-4 py-4 sm:px-6 sm:py-6 lg:px-8">
        <Outlet />
      </main>
    </div>
  );
}
